import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { SelfreflectionQuestion, SelfReflectionQuestionType, SelfreflectionQuestionBoolean } from '../../../types'
import { Row } from 'antd'
import useSelfreflection from './useSelfreflection'
import { SelfreflectionQuestionBooleanTemplate } from './SelfreflectionQuestionBoolean'
import PerText from '../../../components/custom_components/Text'
import Spacer from '../../../components/custom_components/Spacer'
import { AvatarData } from '../appraisal-flow/shared/appraisal-types'
import EmojiRating from '../../../components/formik-component/EmojiRating'
import FormikRatingRadioGroup from '../../../components/formik-component/RatingRadioGroup'
import { FormikTextArea, FormItem } from '../../../components/formik-component'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerSpacer from '../../../components/custom_components/Spacer'
import { SmartAvatar } from '../../avatar/SmartAvatar'

type Props = {
  question: SelfreflectionQuestion
  employeeId: number
  meetingId: number
  disabled?: boolean
  avatarData?: AvatarData
  formName?: string
}

export const SelfreflectionEntry: FC<Props> = ({ formName, question, employeeId, meetingId, disabled, avatarData }) => {
  const [t] = useTranslation()
  const { selfReflection } = useSelfreflection(employeeId, meetingId, question)

  const getQuestionBody = () => {
    switch (question.type) {
      case SelfReflectionQuestionType.BooleanTick:
        return (
          <Row type="flex" justify="center">
            <SelfreflectionQuestionBooleanTemplate
              questionBooleanInput={selfReflection.subItems[0]}
              questionId={selfReflection.id}
              employeeId={employeeId}
              meetingId={meetingId}
              isTick
              disabled={disabled}
              formName={formName}
              subItemPosition={0}
            />
          </Row>
        )
      case SelfReflectionQuestionType.BooleanCheckbox:
        const sorted = [...selfReflection.subItems].sort((a, b) => a.position - b.position)
        return (
          <Spacer ml="l">
            {sorted.map((bool: SelfreflectionQuestionBoolean) => {
              const originalIndex = selfReflection.subItems.findIndex((sub) => sub.id === bool.id)
              return (
                <Spacer key={bool.id} mb="xxs">
                  <SelfreflectionQuestionBooleanTemplate
                    questionBooleanInput={bool}
                    questionId={selfReflection.id}
                    employeeId={employeeId}
                    meetingId={meetingId}
                    disabled={disabled}
                    formName={formName}
                    subItemPosition={originalIndex >= 0 ? originalIndex : 0}
                  />
                </Spacer>
              )
            })}
          </Spacer>
        )
      case SelfReflectionQuestionType.CategorialFaces:
        return (
          <Row type="flex" justify="center">
            <FormItem name={`${formName}.value`}>
              <EmojiRating
                name={`${formName}.value`}
                // this caused the one-off error in pdf generation, do not change this without also changing the pdf template
                // current scale for smiley is 0-4 not 1-5
                currentlySelected={question.value === null ? -1 : question.value}
                disabled={disabled ? true : false}
              />
            </FormItem>
          </Row>
        )
      case SelfReflectionQuestionType.CategorialSlider:
        return (
          <Row type="flex" justify="center">
            <FormItem name={`${formName}.value`} className="fullWidth">
              <FormikRatingRadioGroup name={`${formName}.value`} value={question.value} disabled={disabled} maxRating={10} />
            </FormItem>
          </Row>
        )
    }
  }

  return (
    <div data-e2e-selfreflection-entry={question.textId}>
      <PerText align="center" mb="m">
        {t(`appraisalMeeting.selfReflection.questions.${question.textId}`)}
      </PerText>

      {getQuestionBody()}

      <PerSpacer mt="xl">
        {disabled && question.comment && (
          <PerFlexRow align="middle" className="per-selfreflection-entry_comment">
            <SmartAvatar
              employeeId={avatarData ? avatarData.id : -1}
              firstName={avatarData ? (avatarData.chosenFirstName ? avatarData.chosenFirstName : avatarData.legalFirstName) : undefined}
              lastName={avatarData ? (avatarData.chosenLastName ? avatarData.chosenLastName : avatarData.legalLastName) : undefined}
            />
            <PerText appearance="quote" ml="m">
              "{question.comment}"
            </PerText>
          </PerFlexRow>
        )}
        {!disabled && (
          <FormikTextArea
            autoSize={false}
            data-e2e-selfreflection-comment
            name={`${formName}.comment`}
            placeholder={t('appraisalMeeting.stepSelfReflection.employee.comment')}
            defaultValue={question.comment ? question.comment : ''}
            rows={4}
            style={{ height: '100%' }}
          />
        )}
      </PerSpacer>
    </div>
  )
}
